// Resilient date handling for values coming from the API.
//
// The backend is not consistent about how it serialises timestamps across
// endpoints (snake_case vs camelCase keys, ISO strings vs "YYYY-MM-DD HH:mm:ss"
// vs epoch numbers). A naive `new Date(user.created_at)` therefore silently
// produces an `Invalid Date` for many profiles, which is why every agency was
// rendering as "< 1 month on the platform". These helpers normalise all of
// those shapes and return `null` when a value genuinely can't be parsed, so
// callers can hide the field instead of showing a wrong number.

export function parseApiDate(raw: unknown): Date | null {
  if (raw == null || raw === "") return null;

  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : raw;
  }

  if (typeof raw === "number") {
    // Heuristic: values below 10^12 are almost certainly epoch *seconds*.
    const ms = raw < 1e12 ? raw * 1000 : raw;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof raw !== "string") return null;

  let s = raw.trim();
  if (!s) return null;

  // Bare epoch as a string.
  if (/^\d{9,}$/.test(s)) return parseApiDate(Number(s));

  // "2010-05-01 12:00:00" → "2010-05-01T12:00:00" (Safari/WebKit refuses the
  // space form and returns Invalid Date).
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s)) s = s.replace(" ", "T");

  // "01.05.2010" / "01/05/2010" → ISO.
  const dmy = s.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    s = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }

  const parsed = new Date(s);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// Keys the API has been seen to use for "when this account was created".
const JOIN_DATE_KEYS = [
  "created_at",
  "createdAt",
  "createdat",
  "created",
  "registered_at",
  "registration_date",
  "date_joined",
  "joined_at",
];

export function getUserJoinDate(user: unknown): Date | null {
  if (!user || typeof user !== "object") return null;
  const rec = user as Record<string, unknown>;
  for (const key of JOIN_DATE_KEYS) {
    if (key in rec) {
      const d = parseApiDate(rec[key]);
      if (d) return d;
    }
  }
  return null;
}

/** Whole months between two dates (never negative). */
export function monthsBetween(from: Date, to: Date = new Date()): number {
  let months =
    (to.getFullYear() - from.getFullYear()) * 12 +
    (to.getMonth() - from.getMonth());
  if (to.getDate() < from.getDate()) months -= 1;
  return Math.max(0, months);
}

/** Whole years between a date and now (never negative). */
export function yearsSince(raw: unknown): number {
  const d = parseApiDate(raw);
  if (!d) return 0;
  return Math.floor(monthsBetween(d) / 12);
}

type TFunc = (key: string, opts?: Record<string, unknown>) => string;

/**
 * Localised relative time — "2 ч. назад", "вчера", "5 месяцев назад".
 *
 * Built from i18n strings (the `time.*` keys) rather than
 * Intl.RelativeTimeFormat: several WebViews ship no relative-time CLDR data
 * for Kyrgyz / Kazakh / Uzbek and silently fall back to English, so those
 * users were seeing "5 months ago". Pass the component's `t`.
 */
export function formatTimeAgo(raw: unknown, t: TFunc): string {
  const d = parseApiDate(raw);
  if (!d) return "";

  const sec = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  const min = Math.round(sec / 60);
  const hr = Math.round(sec / 3600);
  const day = Math.round(sec / 86400);
  const month = Math.round(day / 30);
  const year = Math.round(day / 365);

  if (sec < 45) return t("time.now", { defaultValue: "только что" });
  if (min < 60)
    return t("time.minutesAgo", { count: min, defaultValue: "{{count}} мин. назад" });
  if (hr < 24)
    return t("time.hoursAgo", { count: hr, defaultValue: "{{count}} ч. назад" });
  if (day === 1) return t("time.yesterday", { defaultValue: "вчера" });
  if (day < 30)
    return t("time.daysAgo", { count: day, defaultValue: "{{count}} дн. назад" });
  if (month < 12)
    return t("time.monthsAgo", {
      count: month,
      defaultValue: "{{count}} мес. назад",
    });
  return t("time.yearsAgo", { count: year, defaultValue: "{{count}} г. назад" });
}

/** Localised "short month + year", e.g. "окт. 2025". Empty string if unparseable. */
export function formatJoinMonthYear(raw: unknown, locale?: string): string {
  const d = parseApiDate(raw);
  if (!d) return "";
  const lng =
    locale ||
    (typeof localStorage !== "undefined" && localStorage.getItem("lang")) ||
    "ru";
  const intlLocale = lng === "kg" ? "ky-KG" : lng === "kz" ? "kk-KZ" : lng;
  try {
    return new Intl.DateTimeFormat(intlLocale, {
      month: "short",
      year: "numeric",
    }).format(d);
  } catch {
    return new Intl.DateTimeFormat("ru", {
      month: "short",
      year: "numeric",
    }).format(d);
  }
}
